import React from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Plus, Sparkles, Trash2 } from "lucide-react";

const DAY_OPTIONS = [
  { value: "all", label: "Every day" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekends", label: "Weekends" },
];

export const hoursToText = (hours) => {
  const sorted = [...(hours || [])].sort((a, b) => a - b);
  const parts = [];
  let start = null;
  let prev = null;
  sorted.forEach((h) => {
    if (start === null) { start = h; prev = h; return; }
    if (h === prev + 1) { prev = h; return; }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = h; prev = h;
  });
  if (start !== null) parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(", ");
};

export const textToHours = (text) => {
  const out = new Set();
  (text || "").split(",").forEach((chunk) => {
    const [a, b] = chunk.trim().split("-").map((n) => parseInt(n, 10));
    if (Number.isNaN(a)) return;
    const from = Math.max(0, Math.min(23, a));
    const to = Number.isNaN(b) ? from : Math.max(0, Math.min(23, b));
    for (let h = Math.min(from, to); h <= Math.max(from, to); h += 1) out.add(h);
  });
  return [...out].sort((x, y) => x - y);
};

/** Editor for time-of-use bands: name, price, which days, which hours. */
export function TouEditor({ enabled, onEnabledChange, periods, onChange, template }) {
  const update = (i, patch) => onChange(periods.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  return (
    <div className="mt-6 pt-5 border-t border-[#F0F2F1]">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-heading font-semibold text-[#212121]">Time-of-use rates</div>
          <p className="k-sublabel mt-0.5">Price each hour at the tariff that actually applied.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={onEnabledChange} data-testid="tou-toggle" />
      </div>

      {enabled && (
        <>
          <div className="mt-4 space-y-3" data-testid="tou-period-list">
            {periods.length === 0 && <p className="k-sublabel">Add a band, or start from the standard tariff.</p>}
            {periods.map((p, i) => (
              <div key={`period-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`tou-period-${i}`}>
                <Input
                  className="col-span-3"
                  value={p.name}
                  placeholder="On-peak"
                  onChange={(e) => update(i, { name: e.target.value })}
                  data-testid={`tou-name-${i}`}
                />
                <Input
                  className="col-span-2"
                  type="number"
                  step="0.001"
                  min="0"
                  value={p.rate}
                  placeholder="0.158"
                  onChange={(e) => update(i, { rate: e.target.value })}
                  data-testid={`tou-rate-${i}`}
                />
                <select
                  className="col-span-3 k-input text-sm"
                  value={p.days}
                  onChange={(e) => update(i, { days: e.target.value })}
                  data-testid={`tou-days-${i}`}
                >
                  {DAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Input
                  className="col-span-3"
                  value={p.hoursText}
                  placeholder="7-10, 17-18"
                  onChange={(e) => update(i, { hoursText: e.target.value })}
                  data-testid={`tou-hours-${i}`}
                />
                <button
                  onClick={() => onChange(periods.filter((_, idx) => idx !== i))}
                  className="col-span-1 text-[#B91C1C] justify-self-center"
                  data-testid={`tou-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <p className="mt-2 k-sublabel">
            Bands are matched top to bottom — keep the cheapest, catch-all band last. Hours use 0–23.
          </p>

          <div className="mt-3 flex gap-2">
            <button
              onClick={() => onChange([...periods, { name: "", rate: "", days: "all", hoursText: "" }])}
              className="k-btn-outline"
              data-testid="tou-add-period"
            >
              <Plus className="h-3 w-3" /> Add band
            </button>
            <button
              onClick={() => onChange((template || []).map((p) => ({ ...p, rate: String(p.rate), hoursText: hoursToText(p.hours) })))}
              className="k-btn-outline"
              data-testid="tou-use-template"
            >
              <Sparkles className="h-3 w-3" /> Use standard tariff
            </button>
          </div>
        </>
      )}
    </div>
  );
}
